/**
 * AlertFormStyleLoader Component
 * 
 * This component ensures that all necessary CSS for the AlertForm and its steps
 * is properly loaded and applied, regardless of where the AlertForm is rendered.
 * 
 * It dynamically imports the CSS files based on the current theme.
 */

import React, { useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { THEMES } from '../../context/ThemeContext';

/**
 * AlertFormStyleLoader component
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} The children with styles applied
 */
function AlertFormStyleLoader({ children }) {
  const { theme } = useTheme();
  
  useEffect(() => {
    // Dynamically import the CSS files based on the current theme
    const loadStyles = async () => {
      if (theme === THEMES.DARK) {
        // Load dark theme styles
        await import('../../styles/themes/dark/AlertForm/AlertForm.css');
        await import('../../styles/themes/dark/AlertForm/TypeSelection.css');
        await import('../../styles/themes/dark/AlertForm/EventSelection.css');
        await import('../../styles/themes/dark/AlertForm/RuleBuilder.css');
        await import('../../styles/themes/dark/AlertForm/ServerScope.css');
        await import('../../styles/themes/dark/AlertForm/AlertDetails.css');
        await import('../../styles/themes/dark/AlertForm/ActionSelection.css');
        await import('../../styles/themes/dark/AlertForm/ReviewSubmit.css');
        // Load enhanced form controls for dark theme
        await import('../../styles/themes/dark/AlertForm/FormControls.css');
      } else {
        // Load light theme styles
        await import('../../styles/themes/light/AlertForm/AlertForm.css');
        await import('../../styles/themes/light/AlertForm/TypeSelection.css');
        await import('../../styles/themes/light/AlertForm/EventSelection.css');
        await import('../../styles/themes/light/AlertForm/RuleBuilder.css');
        await import('../../styles/themes/light/AlertForm/ServerScope.css');
        await import('../../styles/themes/light/AlertForm/AlertDetails.css');
        await import('../../styles/themes/light/AlertForm/ActionSelection.css');
        await import('../../styles/themes/light/AlertForm/ReviewSubmit.css');
        // Load enhanced form controls for light theme
        await import('../../styles/themes/light/AlertForm/FormControls.css');
      }
    };
    
    loadStyles();
  }, [theme]);
  
  // Create a wrapper div with the appropriate class to ensure CSS scoping works
  return (
    <div className="alert-form-style-wrapper" data-theme={theme}>
      {children}
    </div>
  );
}

export default AlertFormStyleLoader;
